import {useEffect, useState} from 'react';
import dayjs from 'dayjs';

export enum WeekDay {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
}

export function isoDate(date: Date): string {
    return date.toISOString().split('T')[0];
}

export function today(): string {
    return isoDate(new Date());
}

export function dateTimeString(date: Date): string {
    return dayjs(date).format('YYYY-MM-DD HH:mm');
}

/**
 * ISO week date weeks start on Monday, week 1 is the week with Jan 4 (or the first Thursday)
 */
export function weekNumber(date: Date): number {
    const thursday = withDayOfWeek(date, WeekDay.Thursday);
    const thursdayTime = Date.UTC(thursday.getFullYear(), thursday.getMonth(), thursday.getDate());
    const yearStartTime = Date.UTC(thursday.getFullYear(), 0, 1);
    const dayLength = 1000 * 60 * 60 * 24;
    return Math.ceil((((thursdayTime - yearStartTime) / dayLength) + 1) / 7);
}

export function monthsAgo(months: number = 3): string {
    const date = new Date();
    date.setMonth(date.getMonth() - months);
    date.setDate(1);
    return isoDate(date);
}

export function month(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function monthName(month: string, format: string = 'MMMM'): string {
    return dayjs(month, {format: 'YYYY-MM'}).format(format);
}

export function withFirstMonthDay(date: Date): Date {
    const newDate = new Date(date);
    newDate.setDate(1);
    return newDate;
}

export function withLastMonthDay(date: Date): Date {
    const newDate = new Date(date);
    newDate.setMonth(date.getMonth() + 1);
    newDate.setDate(0);
    return newDate;
}

export function withDayOfWeek(date: Date, dayOfWeek: number): Date {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + ((dayOfWeek === WeekDay.Sunday ? 7 : dayOfWeek) - (date.getDay() === WeekDay.Sunday ? 7 : date.getDay())));
    return newDate;
}

export function plusDays(date: Date, days: number): Date {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + days);
    return newDate;
}

export function plusWeeks(date: Date, weeks: number): Date {
    return plusDays(date, weeks * 7);
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole-day difference between two ISO dates (b - a), computed in UTC to avoid DST/timezone drift. */
export function daysBetween(a: string, b: string): number {
    return Math.round((Date.parse(b) - Date.parse(a)) / MS_PER_DAY);
}

export function dateRange(fromDate: Date, toDate: Date): Date[] {
    const days = daysBetween(isoDate(fromDate), isoDate(toDate));
    const step = days >= 0 ? 1 : -1;
    const length = Math.abs(days) + 1;

    return Array.from({length}, (_, i) => plusDays(fromDate, i * step));
}

export const WeekDayFullNames: Record<WeekDay, string> = {
    [WeekDay.Sunday]: 'Sunday',
    [WeekDay.Monday]: 'Monday',
    [WeekDay.Tuesday]: 'Tuesday',
    [WeekDay.Wednesday]: 'Wednesday',
    [WeekDay.Thursday]: 'Thursday',
    [WeekDay.Friday]: 'Friday',
    [WeekDay.Saturday]: 'Saturday',
};

export const WeekDayShortNames: Record<WeekDay, string> = {
    [WeekDay.Sunday]: 'Sun',
    [WeekDay.Monday]: 'Mon',
    [WeekDay.Tuesday]: 'Tue',
    [WeekDay.Wednesday]: 'Wed',
    [WeekDay.Thursday]: 'Thu',
    [WeekDay.Friday]: 'Fri',
    [WeekDay.Saturday]: 'Sat',
};

export function isWeekend(date: Date | string): boolean {
    const day = (typeof date === 'string' ? new Date(date) : date).getDay();
    return day === WeekDay.Saturday || day === WeekDay.Sunday;
}

export function useToday(): string {
    const [todayValue, setTodayValue] = useState(() => today());

    useEffect(() => {
        const checkDate = () => {
            const newToday = today();
            if (newToday !== todayValue) {
                setTodayValue(newToday);
            }
        };

        const interval = setInterval(checkDate, 60000); // Check every minute
        return () => clearInterval(interval);
    }, [todayValue]);

    return todayValue;
}
